class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.deferredSubmitTimer = null;
    this.debouncedOnSubmit = debounce((event) => {
      // Defer callbacks that arrive during a render cycle instead of dropping them
      if (FacetFiltersForm.renderingSince) {
        const elapsed = Date.now() - FacetFiltersForm.renderingSince;
        if (elapsed < 600) {
          clearTimeout(this.deferredSubmitTimer);
          this.deferredSubmitTimer = setTimeout(() => this.onSubmitHandler(event), 600 - elapsed);
          return;
        }
      }
      this.onSubmitHandler(event);
    }, 500);
    this.handleSubmitInput = this.debouncedOnSubmit.bind(this);

    const facetWrapper = this.querySelector("#FacetsWrapperDesktop");
    if (facetWrapper) facetWrapper.addEventListener("keyup", onKeyUpEscape);
  }

  connectedCallback() {
    const facetForm = this.querySelector("form");
    if (facetForm) facetForm.addEventListener("input", this.handleSubmitInput);
    // const filterButton = document.querySelector(
    //   ".collection__filter-trigger.wt-filter__trigger.wt-filter__trigger--drawer",
    // );
    // filterButton?.addEventListener("keydown", (e) => {
    //   if (e.key === "Enter" || e.keyCode === 13) {
    //     document.querySelector("collection-section").toggleDrawer();
    //     document
    //       .querySelector(".svg-icon.svg-icon--close.wt-filter__close")
    //       ?.focus();
    //   }
    // });
    // const checkboxInputs = this.querySelectorAll(".form-checkbox__input");
    // checkboxInputs.forEach((input) => {
    //   input.addEventListener("keydown", (e) => {
    //     if (e.key === "Enter" || e.keyCode === 13) {
    //       input.checked = !input.checked;
    //     }
    //   });
    // });
  }

  disconnectedCallback() {
    const facetForm = this.querySelector("form");
    if (facetForm) facetForm.removeEventListener("input", this.handleSubmitInput);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  static fireCustomEvent(options) {
    const facetsChange = new CustomEvent("facets:change", {
      bubbles: true,
      cancelable: true,
      detail: {
        desc: "facets changed",
        ...options,
      },
    });
    document.dispatchEvent(facetsChange);
  }

  static normalizeFilterValue(value) {
    return String(value || "").trim().toLowerCase();
  }

  static comparableFilterTokens(value) {
    const normalized = FacetFiltersForm.normalizeFilterValue(value);
    if (!normalized) return [];

    const spaced = normalized.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
    const slug = spaced.replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

    return Array.from(new Set([normalized, spaced, slug].filter(Boolean)));
  }

  static paramKeyToAttr(paramKey) {
    const safe = paramKey.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    return `data-vf-${safe}`;
  }

  static getSupportedGenericFilterKeys(grid) {
    const raw = (grid?.dataset.supportedVfKeys || "").toLowerCase().replace(/\s+/g, "");
    if (!raw) return new Set();
    return new Set(raw.split("||").filter(Boolean));
  }

  static getSupportedCountFilterKeys(grid) {
    const raw = (grid?.dataset.supportedCountFilterKeys || "").toLowerCase().replace(/\s+/g, "");
    if (!raw) return new Set();
    return new Set(raw.split("||").filter(Boolean));
  }

  static collectActiveGenericVariantFilters(params, supportedKeys) {
    const groups = {};
    for (const [rawKey, rawVal] of params.entries()) {
      const key = rawKey.toLowerCase();
      if (!key.startsWith("filter.v.")) continue;
      if (key === "filter.v.price.gte" || key === "filter.v.price.lte" || key === "filter.v.availability") continue;
      if (!(key.startsWith("filter.v.option.") || key.startsWith("filter.v.m."))) continue;
      if (supportedKeys && supportedKeys.size && !supportedKeys.has(key)) continue;
      if (!groups[key]) groups[key] = new Set();
      groups[key].add(FacetFiltersForm.normalizeFilterValue(rawVal));
    }
    return Object.fromEntries(
      Object.entries(groups).map(([k, set]) => [k, Array.from(set)])
    );
  }

  static readCardValuesForFilter(card, filterKey) {
    const attr = FacetFiltersForm.paramKeyToAttr(filterKey);
    const raw = card.getAttribute(attr);
    if (!raw) return [];
    const values = [];
    raw.split("||").forEach((token) => {
      FacetFiltersForm.comparableFilterTokens(token).forEach((t) => values.push(t));
    });
    return Array.from(new Set(values));
  }

  static collectActiveCountFilters(params, supportedKeys) {
    const groups = {};
    for (const [rawKey, rawVal] of params.entries()) {
      const key = rawKey.toLowerCase();
      if (!key.startsWith("filter.v.")) continue;
      if (key === "filter.v.price.gte" || key === "filter.v.price.lte" || key === "filter.v.availability") continue;
      if (supportedKeys && supportedKeys.size && !supportedKeys.has(key)) continue;
      if (!groups[key]) groups[key] = new Set();
      groups[key].add(FacetFiltersForm.normalizeFilterValue(rawVal));
    }
    return Object.fromEntries(Object.entries(groups).map(([k, set]) => [k, Array.from(set)]));
  }

  static cardMatchesFilterGroups(card, filterGroups) {
    for (const [filterKey, selectedValues] of Object.entries(filterGroups)) {
      if (!selectedValues.length) continue;
      const cardValues = FacetFiltersForm.readCardValuesForFilter(card, filterKey);
      if (!cardValues.length) return false;

      const cardValueSet = new Set(cardValues);
      let hasMatch = false;
      for (const selectedValue of selectedValues) {
        const candidates = FacetFiltersForm.comparableFilterTokens(selectedValue);
        if (candidates.some((candidate) => cardValueSet.has(candidate))) {
          hasMatch = true;
          break;
        }
      }
      if (!hasMatch) return false;
    }
    return true;
  }

  static updateSplitModeFacetCounts(searchParamsStr) {
    const grid = document.getElementById("product-grid");
    if (!grid || grid.dataset.variantDisplayMode !== "split") return;

    const cards = Array.from(grid.querySelectorAll("[data-variant-card]"));
    if (!cards.length) return;

    const params = new URLSearchParams(searchParamsStr || window.location.search);
    const supportedKeys = FacetFiltersForm.getSupportedCountFilterKeys(grid);
    if (!supportedKeys.size) return;

    const activeCountFilters = FacetFiltersForm.collectActiveCountFilters(params, supportedKeys);
    const filterInputs = Array.from(
      document.querySelectorAll("facet-filters-form .js-filter input.form-checkbox__input[type='checkbox']")
    );
    if (!filterInputs.length) return;

    const priceGte = params.get("filter.v.price.gte");
    const priceLte = params.get("filter.v.price.lte");
    const parsedPriceGte = priceGte !== null && priceGte !== "" ? parseFloat(priceGte) : null;
    const parsedPriceLte = priceLte !== null && priceLte !== "" ? parseFloat(priceLte) : null;
    const rangeMinRaw = parseFloat(grid.dataset.priceRangeMin || "");
    const rangeMaxRaw = parseFloat(grid.dataset.priceRangeMax || "");
    const rangeMin = Number.isFinite(rangeMinRaw) ? rangeMinRaw : null;
    const rangeMax = Number.isFinite(rangeMaxRaw) ? rangeMaxRaw : null;
    const epsilon = 0.0001;
    const minIsActive =
      parsedPriceGte !== null &&
      Number.isFinite(parsedPriceGte) &&
      (rangeMin === null || parsedPriceGte > rangeMin + epsilon);
    const maxIsActive =
      parsedPriceLte !== null &&
      Number.isFinite(parsedPriceLte) &&
      (rangeMax === null || parsedPriceLte < rangeMax - epsilon);
    const priceMin = minIsActive ? Math.round(parsedPriceGte * 100) : null;
    const priceMax = maxIsActive ? Math.round(parsedPriceLte * 100) : null;
    const filterInStockOnly = params.get("filter.v.availability") === "1";

    const candidateKeys = new Map();
    filterInputs.forEach((input) => {
      const filterKey = String(input.name || "").toLowerCase();
      if (!filterKey.startsWith("filter.v.")) return;
      if (filterKey === "filter.v.availability") return;
      if (!supportedKeys.has(filterKey)) return;

      const filterValue = FacetFiltersForm.normalizeFilterValue(input.value);
      if (!filterValue) return;

      const id = `${filterKey}||${filterValue}`;
      if (!candidateKeys.has(id)) candidateKeys.set(id, { filterKey, filterValue });
    });

    candidateKeys.forEach((candidate) => {
      const candidateFilters = Object.fromEntries(
        Object.entries(activeCountFilters).map(([k, arr]) => [k, [...arr]])
      );
      const valuesForKey = new Set(candidateFilters[candidate.filterKey] || []);
      valuesForKey.add(candidate.filterValue);
      candidateFilters[candidate.filterKey] = Array.from(valuesForKey);

      let count = 0;
      cards.forEach((card) => {
        if (!FacetFiltersForm.cardMatchesFilterGroups(card, candidateFilters)) return;

        if (priceMin !== null || priceMax !== null) {
          const variantPrice = parseInt(card.dataset.variantPrice, 10) || 0;
          if (priceMin !== null && variantPrice < priceMin) return;
          if (priceMax !== null && variantPrice > priceMax) return;
        }

        if (filterInStockOnly && card.dataset.variantAvailable !== "true") return;
        count++;
      });

      candidate.count = count;
    });

    filterInputs.forEach((input) => {
      const filterKey = String(input.name || "").toLowerCase();
      const filterValue = FacetFiltersForm.normalizeFilterValue(input.value);
      const id = `${filterKey}||${filterValue}`;
      const candidate = candidateKeys.get(id);
      if (!candidate) return;

      const labelEl = input.closest("label")?.querySelector(".form-checkbox__label");
      if (!labelEl) return;

      const fallbackLabel = labelEl.textContent.replace(/\s*\(\d+\)\s*$/, "").trim();
      const baseLabel = String(input.dataset.label || fallbackLabel).trim();
      labelEl.textContent = `${baseLabel} (${candidate.count})`;
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    // Increment render generation so stale responses are discarded
    FacetFiltersForm.renderGeneration = (FacetFiltersForm.renderGeneration || 0) + 1;
    const currentGeneration = FacetFiltersForm.renderGeneration;

    const sections = FacetFiltersForm.getSections();

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, searchParams)
        : FacetFiltersForm.renderSectionFromFetch(url, event, searchParams, currentGeneration);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event, searchParams, generation) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        // Discard stale response — a newer renderPage call has superseded this one
        if (generation !== FacetFiltersForm.renderGeneration) return;

        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          { html, url },
        ];

        // Mark rendering start so debounced input events queued during DOM replacement are ignored
        FacetFiltersForm.renderingSince = Date.now();
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html, searchParams);
        // FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === "function")
          initializeScrollAnimationTrigger(html.innerHTML);
      })
      .finally(() => FacetFiltersForm.fireCustomEvent());
  }

  static renderSectionFromCache(filterDataUrl, event, searchParams) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;

    // Mark rendering start so debounced input events queued during DOM replacement are ignored
    FacetFiltersForm.renderingSince = Date.now();
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html, searchParams);
    // FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === "function")
      initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html, searchParams) {
    document.getElementById("ProductGridContainer").innerHTML = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("ProductGridContainer").innerHTML;

    // Reset banner stash — fresh HTML has new banner elements
    const grid = document.getElementById("product-grid");
    if (grid) grid._banners = null;

    // Pass the exact search params from the renderPage call that triggered this render
    FacetFiltersForm.postFilterVariantCards(searchParams);
    FacetFiltersForm.reinitSwymButtons();
  }

  static sortVariantCards() {
    const grid = document.getElementById("product-grid");
    if (!grid || grid.dataset.variantDisplayMode !== "split") return;

    const sortBy = grid.dataset.sortBy || "";
    if (!sortBy || sortBy === "manual" || sortBy === "best-selling") return;

    const cards = Array.from(grid.querySelectorAll("[data-variant-card]"));
    if (cards.length === 0) return;

    cards.sort((a, b) => {
      let result = 0;
      switch (sortBy) {
        case "price-ascending":
          result = (parseInt(a.dataset.variantPrice, 10) || 0) - (parseInt(b.dataset.variantPrice, 10) || 0);
          break;
        case "price-descending":
          result = (parseInt(b.dataset.variantPrice, 10) || 0) - (parseInt(a.dataset.variantPrice, 10) || 0);
          break;
        case "title-ascending":
          result = (a.dataset.variantTitle || "").localeCompare(b.dataset.variantTitle || "");
          break;
        case "title-descending":
          result = (b.dataset.variantTitle || "").localeCompare(a.dataset.variantTitle || "");
          break;
        case "created-descending":
          result = (parseInt(b.dataset.variantCreated, 10) || 0) - (parseInt(a.dataset.variantCreated, 10) || 0);
          break;
        case "created-ascending":
          result = (parseInt(a.dataset.variantCreated, 10) || 0) - (parseInt(b.dataset.variantCreated, 10) || 0);
          break;
        default:
          return 0;
      }
      // Stable tiebreaker: fall back to variant title then variant ID
      if (result === 0) {
        result = (a.dataset.variantTitle || "").localeCompare(b.dataset.variantTitle || "");
      }
      if (result === 0) {
        result = (a.dataset.variantId || "").localeCompare(b.dataset.variantId || "");
      }
      return result;
    });

    // Rebuild grid with sorted cards only (banners added later by applyVariantLoadMore)
    while (grid.firstChild) grid.removeChild(grid.firstChild);
    cards.forEach((card) => grid.appendChild(card));
  }

  static postFilterVariantCards(searchParamsStr) {
    const grid = document.getElementById("product-grid");
    if (!grid || grid.dataset.variantDisplayMode !== "split") return;

    // Stash banners before sorting so they can be repositioned among visible cards later
    if (!grid._banners || grid._banners.length === 0) {
      const bannersInDom = Array.from(grid.querySelectorAll("[data-grid-banner]"));
      if (bannersInDom.length > 0) {
        bannersInDom.forEach((b) => b.remove());
        grid._banners = bannersInDom;
      }
    }

    FacetFiltersForm.sortVariantCards();

    // Use provided search params (from renderPage) or fall back to current URL (initial page load)
    const params = new URLSearchParams(searchParamsStr || window.location.search);

    // Special handlers: price (range) and availability (boolean)
    const priceGte = params.get("filter.v.price.gte");
    const priceLte = params.get("filter.v.price.lte");
    const parsedPriceGte = priceGte !== null && priceGte !== "" ? parseFloat(priceGte) : null;
    const parsedPriceLte = priceLte !== null && priceLte !== "" ? parseFloat(priceLte) : null;

    // Treat price as active only when user narrowed from the response range.
    // Price inputs are always present in query params, so we need this guard.
    const rangeMinRaw = parseFloat(grid.dataset.priceRangeMin || "");
    const rangeMaxRaw = parseFloat(grid.dataset.priceRangeMax || "");
    const rangeMin = Number.isFinite(rangeMinRaw) ? rangeMinRaw : null;
    const rangeMax = Number.isFinite(rangeMaxRaw) ? rangeMaxRaw : null;
    const epsilon = 0.0001;

    const minIsActive =
      parsedPriceGte !== null &&
      Number.isFinite(parsedPriceGte) &&
      (rangeMin === null || parsedPriceGte > rangeMin + epsilon);
    const maxIsActive =
      parsedPriceLte !== null &&
      Number.isFinite(parsedPriceLte) &&
      (rangeMax === null || parsedPriceLte < rangeMax - epsilon);

    const priceMin = minIsActive ? Math.round(parsedPriceGte * 100) : null;
    const priceMax = maxIsActive ? Math.round(parsedPriceLte * 100) : null;

    const availabilityFilter = params.get("filter.v.availability");
    const filterInStockOnly = availabilityFilter === "1";

    // Generic variant filters (options, metafields, any future filter.v.* keys)
    // Only match keys the Liquid template actually emitted data-vf-* attrs for
    const supportedKeys = FacetFiltersForm.getSupportedGenericFilterKeys(grid);
    const genericFilters = FacetFiltersForm.collectActiveGenericVariantFilters(params, supportedKeys);

    const variantCards = document.querySelectorAll("[data-variant-card]");
    const hasGenericFilters = Object.keys(genericFilters).length > 0;
    const hasPriceFilter = minIsActive || maxIsActive;
    const hasFilters = hasGenericFilters || hasPriceFilter || filterInStockOnly;

    variantCards.forEach((card) => {
      if (!hasFilters) {
        card.removeAttribute("data-filtered-out");
        return;
      }

      let matches = true;

      // Generic matching: OR within same key, AND across different keys
      if (hasGenericFilters) {
        for (const [filterKey, selectedValues] of Object.entries(genericFilters)) {
          const cardValues = FacetFiltersForm.readCardValuesForFilter(card, filterKey);
          if (!cardValues.length) { matches = false; break; }
          const cardValueSet = new Set(cardValues);
          let hasAnyMatch = false;
          for (const selectedValue of selectedValues) {
            const candidates = FacetFiltersForm.comparableFilterTokens(selectedValue);
            if (candidates.some((candidate) => cardValueSet.has(candidate))) {
              hasAnyMatch = true;
              break;
            }
          }
          if (!hasAnyMatch) { matches = false; break; }
        }
      }

      // Price filter
      if (matches && hasPriceFilter) {
        const variantPrice = parseInt(card.dataset.variantPrice, 10) || 0;
        if (priceMin !== null && variantPrice < priceMin) matches = false;
        if (priceMax !== null && variantPrice > priceMax) matches = false;
      }

      // Availability filter
      if (matches && filterInStockOnly) {
        if (card.dataset.variantAvailable !== "true") matches = false;
      }

      if (matches) {
        card.removeAttribute("data-filtered-out");
      } else {
        card.setAttribute("data-filtered-out", "");
      }
    });

    FacetFiltersForm.updateSplitModeFacetCounts(searchParamsStr);

    // Reset load more state and apply pagination
    FacetFiltersForm.variantCardsShown = 0;
    FacetFiltersForm.applyVariantLoadMore();

    // Reveal grid now that sort/filter/pagination is applied
    grid.classList.remove("collection__grid--loading");
  }

  static applyVariantLoadMore() {
    const grid = document.getElementById("product-grid");
    if (!grid || grid.dataset.variantDisplayMode !== "split") return;

    const cardsPerPage = parseInt(grid.dataset.cardsPerPage, 10) || 24;
    const variantCards = grid.querySelectorAll("[data-variant-card]");
    const loadMoreContainer = document.getElementById("variant-load-more");

    // First remove any banners currently in the grid (they'll be re-inserted at correct positions)
    grid.querySelectorAll("[data-grid-banner]").forEach((b) => b.remove());
    // Collect banners from stash or from DOM
    const banners = grid._banners || [];

    // Count total matching cards first, then compute the visible window
    let totalMatchingCards = 0;
    variantCards.forEach((card) => {
      if (!card.hasAttribute("data-filtered-out")) totalMatchingCards++;
    });

    const nextVisibleLimit = Math.min(
      FacetFiltersForm.variantCardsShown + cardsPerPage,
      totalMatchingCards
    );

    // Show/hide cards based on the visible window
    let visibleIndex = 0;
    variantCards.forEach((card) => {
      if (card.hasAttribute("data-filtered-out")) {
        card.style.display = "none";
        return;
      }
      visibleIndex++;
      card.style.display = visibleIndex <= nextVisibleLimit ? "" : "none";
    });

    FacetFiltersForm.variantCardsShown = nextVisibleLimit;

    // Show or hide load more button
    if (loadMoreContainer) {
      loadMoreContainer.style.display =
        FacetFiltersForm.variantCardsShown < totalMatchingCards ? "" : "none";
    }

    // Re-insert banners at their intended position among VISIBLE cards
    // "position 4" means "before the 4th visible card"
    banners.forEach((banner) => {
      const position = parseInt(banner.dataset.bannerPosition, 10) || 0;
      if (position <= 0 || position > nextVisibleLimit) {
        // Banner position is outside the current visible window — keep hidden
        banner.style.display = "none";
        grid.appendChild(banner);
        return;
      }
      banner.style.display = "";
      let visibleCount = 0;
      const allCards = grid.querySelectorAll("[data-variant-card]");
      for (const card of allCards) {
        if (card.style.display === "none") continue;
        visibleCount++;
        if (visibleCount === position) {
          card.parentNode.insertBefore(banner, card);
          return;
        }
      }
      // Fallback: append hidden
      banner.style.display = "none";
      grid.appendChild(banner);
    });

    // Update product count using i18n templates from data attributes
    const countContainers = document.querySelectorAll("#ProductCount");
    countContainers.forEach((container) => {
      if (container.dataset.countMode === "search" && container.dataset.countSearchTemplate) {
        container.textContent = container.dataset.countSearchTemplate.replace("__COUNT__", totalMatchingCards);
      } else {
        const tpl = totalMatchingCards === 1
          ? container.dataset.countOne
          : container.dataset.countOther;
        container.textContent = tpl
          ? tpl.replace("__COUNT__", totalMatchingCards)
          : `${totalMatchingCards} ${totalMatchingCards === 1 ? "product" : "products"}`;
      }
    });

  }

  static reinitSwymButtons() {
    // Re-initialize Swym wishlist buttons after DOM changes (filters, load more)
    if (window._swat) {
      window._swat.initializeActionButtons("#ProductGridContainer");
    }
  }

  static renderProductCount(html) {
    const count = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("ProductCount").innerHTML;
    const container = document.getElementById("ProductCount");
    const containerDesktop = document.getElementById("ProductCountDesktop");
    container.innerHTML = count;
    container.classList.remove("loading");
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove("loading");
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");

    const facetDetailsElements = parsedHTML.querySelectorAll(
      "#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter",
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest(".js-filter") : undefined;
      return jsFilter
        ? element.dataset.index === jsFilter.dataset.index
        : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter(
      (element) => !matchesIndex(element),
    );
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      // const newElement = document.createElement(element.tagName);
      // newElement.innerHTML = element.innerHTML;
      const newElement = element;

      const tabIndexElements = newElement.querySelectorAll(
        ".wt-collapse__trigger",
      );
      tabIndexElements.forEach((el) => el.setAttribute("tabindex", "0"));

      const container = document.querySelector(
        `.js-filter[data-index="${element.dataset.index}"]`,
      );

      container.innerHTML = ""; // clear existing content
      container.appendChild(newElement); // append new element
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender)
      FacetFiltersForm.renderCounts(
        countsToRender,
        event.target.closest(".js-filter"),
      );
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [".active-facets"];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML =
        activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [
      ".mobile-facets__open",
      ".mobile-facets__count",
      ".sorting",
    ];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML =
        html.querySelector(selector).innerHTML;
    });

    // document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector(".facets__selected");
    const sourceElement = source.querySelector(".facets__selected");

    const targetElementAccessibility = target.querySelector(".facets__summary");
    const sourceElementAccessibility = source.querySelector(".facets__summary");

    if (sourceElement && targetElement) {
      target.querySelector(".facets__selected").outerHTML =
        source.querySelector(".facets__selected").outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector(".facets__summary").outerHTML =
        source.querySelector(".facets__summary").outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`,
    );
  }

  static getSections() {
    return [
      {
        section: document.getElementById("product-grid").dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const q = new URL(location.href).searchParams.get('q'); 
    const formData = new FormData(form);
    if(q && !formData.has('q'))formData.append('q', q);

    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sourceForm = event.target.closest("form");
    if (!sourceForm) return;

    const isMobile = sourceForm.id === "FacetFiltersFormMobile";
    if (isMobile) {
      this.onSubmitForm(this.createSearchParams(sourceForm), event);
      return;
    }

    const sortFilterForms = document.querySelectorAll(
      "facet-filters-form form",
    );
    const forms = [];
    sortFilterForms.forEach((form) => {
      if (
        form.id === "FacetSortForm" ||
        form.id === "FacetFiltersForm" ||
        form.id === "FacetSortDrawerForm"
      ) {
        const noJsElements = document.querySelectorAll(".no-js-list");
        noJsElements.forEach((el) => el.remove());
        forms.push(this.createSearchParams(form));
      }
    });
    this.onSubmitForm(forms.join("&"), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf("?") == -1
        ? ""
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf("?") + 1,
          );
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.renderGeneration = 0;
FacetFiltersForm.renderingSince = 0;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

FacetFiltersForm.variantCardsShown = 0;

document.addEventListener("DOMContentLoaded", () => {
  FacetFiltersForm.postFilterVariantCards();

  document.addEventListener("click", (e) => {
    if (e.target.closest("#variant-load-more-btn")) {
      FacetFiltersForm.applyVariantLoadMore();
      FacetFiltersForm.reinitSwymButtons();
    }
  });
});

// Safety fallback: reveal grid if JS errors prevent postFilterVariantCards from running
setTimeout(() => {
  const grid = document.getElementById("product-grid");
  if (grid) grid.classList.remove("collection__grid--loading");
}, 3000);

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll("input").forEach((element) =>
      element.addEventListener("input", this.onRangeChange.bind(this)),
    );
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll("input");
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute("max", maxInput.value.trim());
    if (minInput.value) maxInput.setAttribute("min", minInput.value.trim());
    if (minInput.value === "") maxInput.setAttribute("min", 0);
    if (maxInput.value === "")
      minInput.setAttribute("max", maxInput.getAttribute("max"));
  }

  adjustToValidValues(input) {
    const fromInput = this.querySelector('[name="filter.v.price.gte"]');
    const toInput = this.querySelector('[name="filter.v.price.lte"]');
    const sliderEl = this.querySelector(".f-price__slider");

    const globalMax = sliderEl
      ? Number(sliderEl.dataset.max) || 999999
      : 999999;

    let fromValue = parseFloat(fromInput.value) || 0;
    let toValue = parseFloat(toInput.value) || 0;

    if (input.name === "filter.v.price.gte") {
      if (fromValue < 0) {
        fromValue = 0;
      }
      if (fromValue > toValue) {
        fromValue = toValue;
      }
      if (fromValue > globalMax) {
        fromValue = globalMax;
      }
      fromInput.value = fromValue;
    } else {
      if (toValue < fromValue) {
        toValue = fromValue;
      }
      if (toValue > globalMax) {
        toValue = globalMax;
      }
      toInput.value = toValue;
    }
  }
}

customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector("a");
    facetLink.setAttribute("role", "button");
    facetLink.addEventListener("click", this.closeFilter.bind(this));
    facetLink.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.closeFilter(event);
      }
    });
    FacetFiltersForm.fireCustomEvent();
  }

  closeFilter(event) {
    event.preventDefault();
    const form =
      this.closest("facet-filters-form") ||
      document.querySelector("facet-filters-form");
    const priceRanges = document.querySelectorAll(
      ".f-price__inputs > .f-price__val",
    );

    if (
      (event.target.className === "filter__remove" ||
        event.target.parentNode.classList.contains("price_range")) &&
      priceRanges
    ) {
      const priceSlider = document.querySelector(".f-price__slider");

      if (priceSlider) {
        const maxPrice = document
          .querySelectorAll("price-range > div")[0]
          .getAttribute("data-max");
        const currency = document
          .querySelectorAll("price-range > div")[0]
          .getAttribute("data-currency");
        const minPriceRange = priceRanges[0].querySelector("input");
        const maxPriceRange = priceRanges[1].querySelector("input");

        priceSlider.noUiSlider.set([
          priceSlider.dataset.min,
          priceSlider.dataset.max,
        ]);

        minPriceRange.value = 0;
        minPriceRange.max = maxPrice;

        maxPriceRange.value = maxPrice;
        maxPriceRange.min = 0;
      }
    }
    form.onActiveFilterClick(event);
  }
}

customElements.define("facet-remove", FacetRemove);
